import { useCallback, useEffect, useRef } from 'react';
import type { CallerProvidedTagsT, CaptureLatencyInputT, PerformanceMetricActionT } from '../../image/labelling-v2/analytics/types/performance';
type CaptureLatencyFn = (input: CaptureLatencyInputT) => void;
interface UseTrackInteractionLatencyOptions<T extends any[]> {
  metricName: PerformanceMetricActionT;
  event: keyof HTMLMediaElementEventMap;
  captureLatency: CaptureLatencyFn;
  generateTag?: (...args: T) => CallerProvidedTagsT;
}

/**
 * Measures interaction-to-DOM-event latency and emits via the usePerformanceMetrics pipeline
 * (M3 histogram with bucketed tags + analytics/Heatpipe with full detail).
 */
const useTrackInteractionLatency = <Tags extends unknown[],>(func: (...args: Tags) => void, element: HTMLElement | null, {
  metricName,
  event,
  captureLatency,
  generateTag
}: UseTrackInteractionLatencyOptions<Tags>): ((...args: Tags) => void) => {
  const functionRef = useRef(func);
  // eslint-disable-next-line react-hooks/refs
  functionRef.current = func;
  const interactionStartRef = useRef<number | null>(null);
  const tagsRef = useRef<CallerProvidedTagsT>({});
  const handleInteractiveFunction = useCallback((...args: Tags) => {
    interactionStartRef.current = performance.now();
    tagsRef.current = generateTag?.(...args) || {};
    functionRef.current(...args);
  }, [generateTag]);
  const captureLatencyRef = useRef(captureLatency);
  // eslint-disable-next-line react-hooks/refs
  captureLatencyRef.current = captureLatency;
  const handleOnInteraction = useCallback(() => {
    if (!interactionStartRef.current) return;
    const latency = performance.now() - interactionStartRef.current;
    interactionStartRef.current = null;
    captureLatencyRef.current({
      name: metricName,
      durationMs: latency,
      tags: tagsRef.current
    });
  }, [metricName]);
  useEffect(() => {
    element?.addEventListener(event, handleOnInteraction);
    return () => {
      element?.removeEventListener(event, handleOnInteraction);
    };
  }, [element, event, handleOnInteraction]);
  return handleInteractiveFunction;
};
export { useTrackInteractionLatency };